// src/services/auditService/adjustmentQueries.ts
import { useQuery } from '@tanstack/react-query';
import {
  queryAdjustByDrCr,
} from '@/services/auditService/auditAdjustmentService';
import {
  queryAdjustmentList,
  type AuditCompanyAnnualQuery,
  type AuditAdjustRecordList,
} from '@/services/auditService/auditAdjustOrchestrate';
import {
  getWorkingPaper,
  type WorkingPaperQueryRequest,
  type WorkingPaperData,
} from '@/services/auditService/auditWorkingPaperService';
import {
  emptyRoundingAdjustGroup,
  roundingAdjustService,
  type RoundingAdjustGroupVO,
} from '@/services/auditService/roundingAdjustService';
import userStore from '@/store/admin/userStore';
import { isWpMetadataOnly } from '@/adjustment/adjust/dialogs/utils/initial-data-factories';
import { resolveWpFetchCode } from '@/adjustment/adjust/dialogs/utils/wpSpecialSyncCodes';

const qk = {
  adjustmentList: (companyId: number, annualId: number) => ['adjustment', 'list', companyId, annualId] as const,
  adjustRecordByWorking: (companyId: number, annualId: number, code: string) => 
    ['adjustment', 'workingRecord', companyId, annualId, code] as const,
  adjustByDrCr: (companyId: number, annualId: number, refNo: string) => 
    ['adjustment', 'drCr', companyId, annualId, refNo] as const,
  roundingAdjust: (annualId: number) => ['rounding-adjust', annualId] as const,
};

/**
 * Query the integrated adjustment list with FS data via audit-orchestrator
 */
export function useQueryAdjustmentList(companyId?: number, annualId?: number, inCorpDate?: string, enabled: boolean = true) {
  const cid = Number(companyId || 0) || 0;
  const aid = Number(annualId || 0) || 0;
  return useQuery<AuditAdjustRecordList>({
    queryKey: qk.adjustmentList(cid, aid),
    enabled: enabled && cid > 0 && aid > 0,
    queryFn: async () => {
      const body: AuditCompanyAnnualQuery = {
        companyId: cid,
        annualId: aid,
        userId: userStore.getCurrentUserId(),
        status: [0, 1],
      };
      return await queryAdjustmentList(body);
    },
    staleTime: 60_000,
  });
}

/**
 * Hook to query adjustment record by working paper code
 * Now using the new working-paper/get API endpoint
 * Updated to use code directly instead of scheduleCode
 * Returns detail data directly instead of header/line/detail structure
 */
export function useQueryAdjustRecordByWorking(
  companyId?: number, 
  annualId?: number, 
  code?: string,  
  enabled: boolean = true
) {
  const cid = Number(companyId || 0) || 0;
  const aid = Number(annualId || 0) || 0;
  const uiCode = (code || '').trim();
  const apiCode = resolveWpFetchCode(uiCode);
  
  return useQuery<WorkingPaperData>({  
    queryKey: qk.adjustRecordByWorking(cid, aid, uiCode),
    enabled: enabled && cid > 0 && aid > 0 && uiCode.length > 0,
    queryFn: async () => {
      const params: WorkingPaperQueryRequest = {
        companyId: cid,
        annualId: aid,
        code: apiCode,
      };
      const result = await getWorkingPaper(params);
      // API returns array, but we return the first item directly (the detail)
      if (!Array.isArray(result) || result.length === 0) return null;
      const item = result[0];
      // 当后端仅返回 WOKE_DONE / SME-FRS 等元数据时，视为空
      if (isWpMetadataOnly(item)) return null;
      return item ?? null;
    },
    staleTime: 30_000,
  });
}

/**
 * 查询 rounding adjustment 记录（带 React Query 缓存）
 * staleTime: 5 分钟 — rounding 数据变更频率低
 */
export function useQueryRoundingAdjust(annualId?: number, enabled: boolean = true) {
  const aid = Number(annualId || 0) || 0;
  return useQuery<RoundingAdjustGroupVO>({
    queryKey: qk.roundingAdjust(aid),
    enabled: enabled && aid > 0,
    queryFn: () => roundingAdjustService.query(aid),
    placeholderData: () => emptyRoundingAdjustGroup(aid),
    staleTime: 5 * 60_000,
    gcTime:   10 * 60_000,
  });
}

/**
 * Query adjustment by DR/CR
 */
export function useQueryAdjustByDrCr(
  companyId?: number, 
  annualId?: number, 
  refNo?: string,
  enabled: boolean = true
) {
  const cid = Number(companyId || 0) || 0;
  const aid = Number(annualId || 0) || 0;
  const ref = (refNo || '').trim();
  
  return useQuery({
    queryKey: qk.adjustByDrCr(cid, aid, ref),
    enabled: enabled && cid > 0 && aid > 0 && ref.length > 0,
    queryFn: async () => {
      return await queryAdjustByDrCr(cid, aid, ref);
    },
    staleTime: 30_000,
  });
}
